import { useRef, useState, useEffect } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UploadImg from "../../assests/upload.svg";
import {
  Typography,
  Paper,
  Grid,
  Button,
  Backdrop,
  CircularProgress,
  Box,
} from "@mui/material";
import DialogBox from "../DialogBox";
import CreditsRequestsForm from "../DialogBox/DialogContents/CreditsRequestForm";
import ThankyouMessage from "../DialogBox/DialogContents/ThankyouMessage";
import { fetchWithTimeout } from "../../utils/fetchWithTimeout";
import { auth } from "../../utils/FireBaseConfigurations/config";


const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";


const FileUploader = ({
  isLoggedIn,
  onCreditsUpdate,
  userCredits,
  onRegisterClick,
  onSessionExpired,
}) => {
  const inputRef = useRef();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [image, setImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageUploaded, setImageUploaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [verificationError, setVerificationError] = useState("");
  const [open, setOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState("request for credits");
  const [showLoginMessage, setShowLoginMessage] = useState(false);
  const [isFormAlreadySubmitted, setIsFormAlreadySubmitted] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const handleCreditsRequest = () => {
    setOpen(true);
    setDialogContent("request for credits");
  };
  const handleClose = () => {
    setOpen(false);
  };


  const handleNext = () => {
    setDialogContent("thank you message");
  };
  const refreshBackendToken = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("No user logged in");
      }


      const providerId = user.providerData?.[0]?.providerId || "password";
      let response;
      if (providerId === "password") {
        console.info(
          "[Auth] Attempting access token refresh using refresh_token (email/password user)"
        );
        const storedRefresh = localStorage.getItem("refresh_token");
        if (!storedRefresh) throw new Error("PASSWORD_USER_RELOGIN_REQUIRED");
        response = await fetchWithTimeout(
          `${BACKEND_URL}/auth/refresh`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ refresh_token: storedRefresh }),
          },
          30000
        );
      } else {
        console.info(
          "[Auth] Attempting silent re-login using OAuth UID (provider:",
          providerId,
          ")"
        );
        response = await fetchWithTimeout(
          `${BACKEND_URL}/auth/loginUser`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email: user.email, password: user.uid }),
          },
          30000
        );
      }


      if (!response.ok) {
        throw new Error("Failed to refresh token");
      }


      const data = await response.json();
      const newAccessToken = data.access_token;
      localStorage.setItem("access_token", newAccessToken);
      if (data.refresh_token) {
        localStorage.setItem("refresh_token", data.refresh_token);
      }


      return newAccessToken;
    } catch (error) {
      console.error("Error refreshing token:", error);
      throw error;
    }
  };


  const resetPreview = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImageFile(null);
    setImagePreviewUrl(null);
    setImageUploaded(false);
  };


  const handleDragOver = (e) => {
    e.preventDefault();
  };


  const handleDrop = (e) => {
    e.preventDefault();
    if (!isLoggedIn) {
      setShowLoginMessage(true);
      return;
    }
    const file = e.dataTransfer.files?.[0];
    if (file) {
       const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type.toLowerCase())) {
        setVerificationError("Only JPEG, JPG and PNG images are supported");
        setImageError(true);
        setImageFile(null);
        setImageUploaded(false);
        return;
      }
      if (showLoginMessage) {
        setShowLoginMessage(false);
      }
      if (verificationResult) {
        setVerificationResult(null);
      }
      if (verificationError) {
        setVerificationError("");
      }
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreviewUrl(previewUrl);
      setImageUploaded(true);
      setImageError(false);
    } else {
      setImageError(true);
    }
  };


  const handleClick = () => {
    if (inputRef.current) {
      if (!isLoggedIn) {
        setShowLoginMessage(true);
        return;
      }
      inputRef.current.click();
    }
  };
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (showLoginMessage) {
        setShowLoginMessage(false);
      }
      if (verificationResult) {
        setVerificationResult(null);
      }
      if (verificationError) {
        setVerificationError("");
      }
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreviewUrl(previewUrl);
      setImageUploaded(true);
      setImageError(false);
    } else {
      setImageError(true);
    }
 
    // Allow selecting the same file again by resetting the input value
    if (e.target) {
      e.target.value = "";
    }	  
  };


  const handleFaceAuthenticity = async () => {
    if (!imageFile) {
      setImageError(true);
      return;
    }


    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) {
      setVerificationError("Please log in to verify images");
      return;
    }


    if (userCredits <= 0) {
      handleCreditsRequest();
      return;
    }


    setLoading(true);
    setVerificationError("");


    try {
      const formData = new FormData();
      formData.append("image", imageFile, `image_${Date.now()}.png`);


      const response = await fetchWithTimeout(
        `${BACKEND_URL}/face/faceAuthenticity`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        },
        30000
      );


      if (!response.ok) {
        if (response.status === 401) {
          console.log("Token expired, attempting automatic refresh...");
          try
	    {
              await refreshBackendToken();
              window.location.reload();
              return;
             } catch (refreshError) {
            console.error("Auto-refresh failed:", refreshError);
            setSessionExpired(true);
            if (onSessionExpired) {
              setVerificationError("");
              onSessionExpired();
            } else {
            setVerificationError("Session expired - please sign in to continue");
	    }
            return;
          }
        }


        if (response.status === 429) {
          setVerificationError("Too many requests. Please wait and try again.");
          return;
        }


        let errorMessage = "Failed to verify image";
        try {
          const errorPayload = await response.json();
          errorMessage = errorPayload.detail || errorPayload.message || errorMessage;
        } catch (_) {
          // ignore parse errors
        }


        throw new Error(errorMessage);
      }


      const data = await response.json();
      handleDetectionSuccess(data);
    } catch (err) {
      console.error("Face authenticity verification error:", err);
     if (err.message === "SESSION_EXPIRED") {
	setSessionExpired(true);     
      if (onSessionExpired) {
          setVerificationError("");
          onSessionExpired();
        } else {
        setVerificationError("Session expired - please sign in again");
	}
      } else if (err.name === "TimeoutError") {
        setVerificationError("Request timeout - please try again");
      } else {
        setVerificationError(err.message || "Failed to verify image");
      }
    } finally {
      setLoading(false);
    }
  };


  const handleDetectionSuccess = (payload) => {
    if (!payload?.success) {
      setVerificationError(
        payload?.message || "Failed to get verification results"
      );
      setVerificationResult(null);
      return;
    }


    const predictionText = (payload.prediction || "").toLowerCase();
    const isAuthentic =
      payload.is_authentic !== null && payload.is_authentic !== undefined
        ? payload.is_authentic
        : predictionText.includes("real") || predictionText.includes("authentic");


    const confidenceRaw =
      payload.confidence_score ??
      payload.confidence ??
      payload.deepfake_result?.confidence_score ??
      payload.deepfake_result?.confidence;


    const confidenceValue =
      confidenceRaw !== undefined && confidenceRaw !== null
        ? parseFloat(confidenceRaw)
        : null;


    setVerificationResult({
      message: payload.message || payload.deepfake_result?.message || "",
      confidence: confidenceValue,
      isAuthentic,
    });


    setVerificationError("");


    if (payload.remaining_credits !== undefined && onCreditsUpdate) {
      onCreditsUpdate();
    }
  };


  const handleUploadAnother = () => {
    resetPreview();
    setVerificationResult(null);
    setVerificationError("");
    setImageError(false);
  };


  useEffect(() => {
    if (isLoggedIn) {
      setShowLoginMessage(false);
    } else {
      resetPreview();
      setVerificationResult(null);
      setVerificationError("");
      setImageError(false);
      setLoading(false);
      setOpen(false);
    }
  }, [isLoggedIn]);


  return (
    <>
      {loading && (
        <div>
          <Backdrop
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
            open={true}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </div>
      )}


      <Paper
        elevation={0}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          border: "2px dashed #ccc",
          borderRadius: "10px",
          p:2,
	  textAlign: "center",
          cursor: isLoggedIn ? "pointer" : "not-allowed",
          width: "100%",
          minHeight: { xs: "115px", sm: "135px", md: "155px", lg: "175px" },
          height: "auto",
          mx: "auto",
          alignSelf: "center",
          boxShadow: "none",
          transition: "border-color 0.3s ease",
          "&:hover": {
            borderColor: "#2196f3",
          },
        }}
      >
        <input
          type="file"
          accept="image/png, image/jpeg, image/jpg"
          ref={inputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />


        {!imageUploaded ? (
          <div onClick={handleClick} style={{ cursor: "pointer" }}>
            <img
              src={UploadImg || CloudUploadIcon}
              alt="Upload"
              style={{
                width: "100%",
                maxWidth: "120px",
                height: "auto",
                display: "block",
                margin: "4px auto 16px",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                color: "#fff",
                lineHeight: 1.5,
                fontSize: { xs: "11px", sm: "12px", md: "13px", lg: "14px" },
                mb: 0.5,
              }}
            >
              <span style={{ fontWeight: "bold" }}>Click to browse</span>, or{" "}
              <span style={{ fontWeight: "bold" }}>Drag & drop a file here</span>
            </Typography>
          </div>
        ) : (
          <>
            {imagePreviewUrl && (
              <img
                src={imagePreviewUrl}
                alt="Uploaded"
                style={{ width: "50%", borderRadius: "8px" }}
              />
            )}
            {verificationResult && (
              <Box>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={verificationResult.isAuthentic ? "success" : "error"}
                  textAlign="center"
                  gutterBottom
                >
                  {verificationResult.isAuthentic
                    ? "Authentic Image"
                    : "Deepfake or AI-Generated Image"}
                </Typography>
                
                <Typography
                  variant="body2"
                  textAlign="center"
                  sx={{ color: "#fff" }}
                >
                 {verificationResult.confidence !== null &&
                  verificationResult.confidence !== undefined
		  ? `Confidence Score: ${
                        verificationResult.confidence > 1
                          ? verificationResult.confidence.toFixed(1)
                          : (verificationResult.confidence * 100).toFixed(1)
                      }%`
                    : "Confidence: Not available"}
                </Typography>
              </Box>
            )}
            {verificationError && (
              <Typography
                variant="body2"
                color="error"
                textAlign="center"
                sx={{ mt: 2 }}
              >
                {verificationError}
              </Typography>
            )}
            <Box
              sx={{
                mt: 3,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              {userCredits > 0 ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      borderRadius: "25px",
                      borderColor: "#2586af",
                      color: "#2586af",
                      fontWeight: "bold",
                      minWidth: "120px",
                      "&:hover": {
                        borderColor: "#1f5a7a",
                        backgroundColor: "rgba(37, 134, 175, 0.1)",
                      },
                    }}
                    onClick={handleClick}
                  >
                    Upload
                  </Button>


                  <Button
                    variant="contained"
                    size="small"
                    disabled={loading || verificationResult !== null}
		    sx={{
                      borderRadius: "25px",
                      background:
                        "linear-gradient(to top, #2586af 0%, #27abe2 100%)",
                      color: "#fff",
                      fontWeight: "bold",
                      minWidth: "120px",
                      "&:disabled": {
                        backgroundColor: "#ccc",
			background: "none",
                        color: "#666",
                      cursor: "not-allowed",
                        opacity: 0.6,
                        "&:hover": {
                          backgroundColor: "#ccc",
                          background: "none",
                        },
                      },
                      "&:hover:not(:disabled)": {background:
                          "linear-gradient(to top, #1f5a7a 0%, #27abe2 100%)",
		      },
                    }}
                    onClick={handleFaceAuthenticity}
                  >
                    {loading ? "Checking..." : "Check"}
                  </Button>
                </>
              ) : (
		!sessionExpired && (      
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    borderRadius: "25px",
                    backgroundColor: "#ff6b6b",
                    color: "#fff",
                    fontWeight: "bold",
                    minWidth: "200px",
                    fontSize: "12px",
                    "&:hover": {
                      backgroundColor: "#ff5252",
                    },
                  }}
                  onClick={handleCreditsRequest}
                >
                  No credits - click here to get more credits
                </Button>
              )
		)}
            </Box>
          </>
        )}


        {showLoginMessage && (
          <Typography
            variant="body2"
            mt={1}
            textAlign="center"
            sx={{ color: "#fff" }}
          >
            Please{" "}
            <Button
              variant="text"
              onClick={onRegisterClick}
              sx={{
                p: 0,
                minWidth: "auto",
                textTransform: "none",
                color: "#28ABE2",
                fontWeight: "bold",
              }}
            >
              Register
            </Button>{" "}
            to continue
          </Typography>
        )}
	{!imageUploaded && verificationError && (
          <Typography
            variant="body2"
            color="error"
            textAlign="center"
            sx={{ mt: 2 }}
          >
            {verificationError}
          </Typography>
        )}  
      </Paper>


        <DialogBox
        open={open}
        onClose={handleClose}
        maxWidth={isFormAlreadySubmitted ? "500px" : "900px"}
      >
	  {dialogContent === "request for credits" ? (
          <CreditsRequestsForm
            onClose={handleClose}
            onCreditsRefetch={onCreditsUpdate}
          onStatusChange={(status) => setIsFormAlreadySubmitted(status.isAlreadySubmitted)}
		  />
        ) : (
          <ThankyouMessage />
        )}
      </DialogBox>
    </>
  );
};


export default FileUploader;
